import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export const userService = {
  // Get user data
  async getUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  },

  // Create or update user data
  async updateUserData(uid, data) {
    try {
      // Create a clean copy of the data
      const cleanData = JSON.parse(JSON.stringify(data));

      // Remove File object from avatar if it exists
      if (cleanData.profile?.avatar?.file) {
        delete cleanData.profile.avatar.file;
      }

      await setDoc(doc(db, 'users', uid), {
        ...cleanData,
        updatedAt: new Date().toISOString(),
        createdAt: cleanData.createdAt || new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user data:', error);
      throw error;
    }
  },

  // Check if user has completed onboarding
  async hasCompletedOnboarding(uid) {
    try {
      const userData = await this.getUserData(uid);
      if (!userData) {
        return false;
      }
      return userData.hasCompletedOnboarding || false;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      throw error;
    }
  },

  // Initialize user data for new users
  async initializeNewUser(uid, userData) {
    try {
      // Create a clean copy of the data
      const cleanData = JSON.parse(JSON.stringify(userData));

      // Remove File object from avatar if it exists
      if (cleanData.profile?.avatar?.file) {
        delete cleanData.profile.avatar.file;
      }

      await this.updateUserData(uid, {
        ...cleanData,
        hasCompletedOnboarding: false,
        createdAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error initializing user data:', error);
      throw error;
    }
  },

  // Update user interests
  async updateUserInterests(userId, interests) {
    try {
      await setDoc(doc(db, 'users', userId), {
        interests,
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user interests:', error);
      throw error;
    }
  },

  // Initialize trial period
  async initializeTrial(userId) {
    try {
      const now = new Date();
      const trialEndDate = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days from now
      
      const subscriptionData = {
        subscription: {
          isActive: false, // Not subscribed yet
          isTrialActive: true,
          trialStartDate: now.toISOString(),
          trialEndDate: trialEndDate.toISOString(),
          status: 'trialing',
          currentPeriodEnd: trialEndDate.toISOString(),
          subscriptionId: null,
          customerId: null,
          priceId: null,
          cancelAtPeriodEnd: false
        },
        updatedAt: now.toISOString()
      };

      await setDoc(doc(db, 'users', userId), subscriptionData, { merge: true });

      return subscriptionData.subscription;
    } catch (error) {
      console.error('Error initializing trial:', error);
      throw error;
    }
  },

  // Check trial status
  async checkTrialStatus(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();

      if (!userData?.subscription) {
        return null;
      }

      const { trialStartDate, trialEndDate, isTrialActive } = userData.subscription;
      const now = new Date();
      const endDate = new Date(trialEndDate);
      const startDate = new Date(trialStartDate);

      // Calculate remaining days
      const remainingDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

      return {
        isActive: isTrialActive && remainingDays > 0,
        remainingDays: Math.max(0, remainingDays),
        startDate,
        endDate
      };
    } catch (error) {
      console.error('Error checking trial status:', error);
      throw error;
    }
  },

  async setAdminRole(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, { role: 'admin' }, { merge: true });
    } catch (error) {
      console.error('Error setting admin role:', error);
      throw error;
    }
  }
}; 